import React from 'react'
import App from 'next/app'
import { DefaultSeo } from 'next-seo'
import NProgress from '~/components/NProgress'
import Router from 'next/router'
import { ToastProvider } from '@ticketswap/solar'
import BaseStyles from '../components/BaseStyles'
import { setTrackingIdentity, page, pageSource, trackExperiments } from '~/utils'
import ViewerProvider from '../components/ViewerContext'
import ConsentDialog from '../components/ConsentDialog/dynamic'
import NetworkStatus from '../components/NetworkStatus'
import { captureException } from '../lib/sentry'
import createSeoConfig from '../lib/next-seo-defaults'
import { withTranslation, appWithTranslation } from '~/i18n'
import { withServerResponse } from '../components/ServerResponseContext'
import getConfig from 'next/config'
import LocaleBar from '../components/LocaleBar/dynamic'
import getViewer from '~/lib/get-viewer'
import { NativeBridge } from '../utils/nativeBridge'
import { LanguageProvider } from '~/components/LanguageContext'
import getLanguageFromHostname from '../utils/i18n/getLanguageFromHostname'
import getLocaleFromHostname from '../utils/i18n/getLocaleFromHostname'
import getDeviceInfo from '~/utils/getDeviceInfo'
import { decodeGlobalId } from '~/utils/id'
import { withApollo } from '../lib/apollo'
import VoucherToast from '~/components/VoucherToast/dynamic'
import CookieToast from '~/components/CookieToast/dynamic'
import { LocaleProvider } from '~/components/LocaleContext'
import { IntercomProvider } from '~/components/IntercomProvider'
import FacebookPixelProvider from '~/components/Pixel'
import FacebookProvider from '~/components/FacebookContext'

const {
  FACEBOOK_APP_ID,
  USER_ID_COOKIE_NAME,
  INTERCOM_APP_ID,
  FACEBOOK_PIXEL_ID,
} = getConfig().publicRuntimeConfig

Router.events.on('routeChangeComplete', path => {
  // Track subsequent page-views
  page(path)
  // Trigger & track Google Optimize experiments after client-side
  // navigation.
  trackExperiments()
})

class MyApp extends App {
  static async getInitialProps({ Component, ctx }) {
    const deviceInfo = getDeviceInfo(ctx.req) || {}
    const viewer = await getViewer(ctx.apolloClient, ctx.req)

    if (viewer && ctx.res) {
      ctx.res.cookie(USER_ID_COOKIE_NAME, decodeGlobalId(viewer.id), {
        secure: true,
        sameSite: 'lax',
      })
    }

    // Initiate vars to return
    let pageProps = { namespacesRequired: ['common'] }

    // Recompile pre-existing pageProps
    try {
      if (Component.getInitialProps) {
        pageProps = await Component.getInitialProps(ctx)
      }
    } catch (e) {
      console.log('------Error on Component.getInitialProps', e, ctx?.req?.path)
      captureException(e, 'appjsGetInitialProps', ctx)
      throw e // We can also skip re-throwing and set property on pageProps
    }

    const currentUrl = ctx?.req
      ? `${ctx.req.headers['x-forwarded-proto']}://${ctx.req.get('host')}`
      : `${window.location.protocol}//${window.location.host}`
    const openGraphUrl = ctx?.req
      ? `${currentUrl}${ctx.req.originalUrl}`
      : `${currentUrl}${window.location.pathname}`

    // `pageProps` will get serialized automatically by NextJs
    return {
      viewer,
      deviceInfo,
      pageProps,
      localeFromRequest: ctx?.req
        ? getLocaleFromHostname(ctx.req.headers.host)
        : getLocaleFromHostname(window.location.hostname),
      languageFromRequest: ctx?.req ? getLanguageFromHostname(ctx.req.headers.host) : null,
      openGraphUrl,
      currentUrl,
    }
  }

  componentDidMount() {
    // This will fix the native browser back-button: When user uses
    // the back navigation of the browser and goes from Sirius to
    // Website it results in the next/router not being able to
    // navigate back because the url is not in memory of the router.
    // Therefore we should just reload the page instead of using
    // next/router to navigate.

    // TODO: remove this when all routes are handled by Sirius.
    Router.beforePopState(({ url, as }) => {
      if (!url && !as) {
        // Reload page if page to go back to is a Website page.
        window.location.reload(false)
        return false
      }

      return true
    })

    const { viewer } = this.props
    viewer && setTrackingIdentity(viewer, false)

    // Track initial page-view
    page(Router.router.asPath)

    // Track referrer-info once per session
    pageSource(Router.router.asPath)

    // Trigger & track Google Optimize experiments on initial page-load
    trackExperiments(true)
  }

  // This reports errors thrown while rendering components
  componentDidCatch(error, errorInfo) {
    console.log('------ Component did catch on _app.js')
    captureException(error, 'appjsComponentDidCatch', { extra: errorInfo })
    super.componentDidCatch(error, errorInfo)
  }

  render() {
    const {
      Component,
      viewer,
      deviceInfo,
      pageProps,
      t,
      localeFromRequest,
      languageFromRequest,
      i18n,
      openGraphUrl,
      currentUrl,
    } = this.props
    const seoDescription = t('common:site.description')
    const languageLocale = languageFromRequest || i18n.language || 'en'
    const language = languageLocale.split('-')[0]

    return (
      <LanguageProvider value={language}>
        <LocaleProvider value={localeFromRequest}>
          <NProgress />
          <BaseStyles />
          <NativeBridge />
          <DefaultSeo {...createSeoConfig(seoDescription, openGraphUrl)} />
          <FacebookProvider appId={FACEBOOK_APP_ID}>
            <FacebookPixelProvider pixelId={FACEBOOK_PIXEL_ID}>
              <ViewerProvider data={{ viewer, deviceInfo, currentUrl }}>
                <IntercomProvider appId={INTERCOM_APP_ID}>
                  <ToastProvider>
                    <VoucherToast t={t} />
                    <ConsentDialog />
                    <LocaleBar language={language} />
                    <NetworkStatus />
                    <Component {...pageProps} />
                    {!deviceInfo.isApp && <CookieToast t={t} />}
                  </ToastProvider>
                </IntercomProvider>
              </ViewerProvider>
            </FacebookPixelProvider>
          </FacebookProvider>
        </LocaleProvider>
      </LanguageProvider>
    )
  }
}

export default withApollo({ ssr: true })(
  appWithTranslation(withServerResponse(withTranslation(['common'])(MyApp)))
)
